import { createFileRoute, Link } from '@tanstack/react-router'
import { useEvents } from '@/lib/api/queries'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, ChevronLeft, ChevronRight, Star } from 'lucide-react'
import { useState } from 'react'
import { formatInEventZone } from '@/lib/eventTime'
import type { Event } from '@/lib/api/types'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { useAuth } from '@/lib/auth'
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value'

const PAST_PAGE_SIZE = 20

export const Route = createFileRoute('/_authenticated/events/')({
  component: EventsPage,
})

function EventsPage() {
  // Permission check - view_event is required to see events list
  const canViewEvents = usePermission('can_view_events')
  const { user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false
  
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const [pastPage, setPastPage] = useState(1)
  const [lastSearch, setLastSearch] = useState(debouncedSearch)
  const isSearching = debouncedSearch.trim().length > 0
  const permissionFilter = isSuperuser ? {} : { has_permission: 'can_view_dashboard' }

  if (lastSearch !== debouncedSearch) {
    setLastSearch(debouncedSearch)
    setPastPage(1)
  }

  const { data: upcomingData, isLoading: upcomingLoading, error: upcomingError } = useEvents({
    search: debouncedSearch || undefined,
    page_size: 100,
    ...permissionFilter,
  })

  const { data: pastData, isLoading: pastLoading, error: pastError } = useEvents({
    search: debouncedSearch || undefined,
    past: 1,
    ordering: '-start_date_time',
    limit: isSearching ? 100 : PAST_PAGE_SIZE,
    offset: isSearching ? 0 : (pastPage - 1) * PAST_PAGE_SIZE,
    ...permissionFilter,
  })

  // Client-side name filter. The API's `search` param is sent but currently
  // not respected by the backend, so filter by event name here.
  const normalizedSearch = debouncedSearch.trim().toLowerCase()
  const matchesSearch = (e: Event) =>
    !normalizedSearch || e.name.toLowerCase().includes(normalizedSearch)

  const upcoming = (upcomingData?.results || [])
    .filter(matchesSearch)
    .slice()
    .sort((a, b) => {
      const dateA = a.start_date_time || a.start_date
      const dateB = b.start_date_time || b.start_date
      return (dateA ? new Date(dateA).getTime() : 0) - (dateB ? new Date(dateB).getTime() : 0)
    })

  const past = (pastData?.results || [])
    .filter(matchesSearch)
    .slice()
    .sort((a, b) => {
      const dateA = a.start_date_time || a.start_date
      const dateB = b.start_date_time || b.start_date
      return (dateB ? new Date(dateB).getTime() : 0) - (dateA ? new Date(dateA).getTime() : 0)
    })

  if (!canViewEvents) {
    return <AccessDenied message="You don't have permission to view events." />
  }

  const isLoading = upcomingLoading || pastLoading
  const error = upcomingError || pastError
  const pastTotalCount = pastData?.count || 0
  const pastTotalPages = Math.ceil(pastTotalCount / PAST_PAGE_SIZE)
  const hasNoEvents = !upcoming.length && !past.length
  const showPastPagination = !isSearching && pastTotalPages > 1

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8">
      <div className="min-h-[calc(100vh-8rem)] flex flex-col py-8">
        {/* Search Section */}
        <div className="flex flex-col sm:flex-row w-full items-center mb-6 gap-3">
          <div className="relative w-full">
            <Input
              type="text"
              placeholder="Search events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full py-2.5 px-4 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#131313] text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-700 dark:focus:ring-neutral-300 focus:border-transparent"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <Search className="h-5 w-5 text-neutral-400" />
            </div>
          </div>
        </div>

        {/* Events Header Section */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">Events</h1>
          <p className="text-neutral-700 dark:text-neutral-300">Select an event you want to manage</p>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-full bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm animate-pulse"
              >
                <div className="flex items-start p-4 gap-3">
                  <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 bg-neutral-200 dark:bg-neutral-800 rounded-md" />
                  <div className="flex-grow">
                    <div className="h-5 bg-neutral-200 dark:bg-neutral-800 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="mt-8 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-400">Error loading events: {error.message}</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && hasNoEvents && (
          <div className="mt-8 text-center py-12 px-4 border border-dashed border-neutral-200 dark:border-neutral-800 rounded-lg">
            <svg
              className="w-16 h-16 mx-auto text-neutral-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-neutral-900 dark:text-neutral-100">No events found</h3>
            <p className="mt-2 text-neutral-600 dark:text-neutral-400">
              {search
                ? `No events match your search criteria "${search}"`
                : "You don't have any events yet"}
            </p>
          </div>
        )}

        {/* Upcoming Events Section */}
        {!isLoading && !error && upcoming.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" />
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">Upcoming</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {upcoming.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )}

        {/* Past Events Section */}
        {!isLoading && !error && past.length > 0 && (
          <div className="mt-10 space-y-4">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-400 mr-2" />
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">Past Events</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {past.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>

            {showPastPagination && (
              <div className="flex items-center justify-between pt-2">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Showing {((pastPage - 1) * PAST_PAGE_SIZE) + 1} to {Math.min(pastPage * PAST_PAGE_SIZE, pastTotalCount)} of {pastTotalCount.toLocaleString()} events
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPastPage(p => Math.max(1, p - 1))}
                    disabled={pastPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPastPage(p => Math.min(pastTotalPages, p + 1))}
                    disabled={pastPage === pastTotalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function EventCard({ event }: { event: Event }) {
  // Helper to get event date (prefer *_date_time fields from API)
  const startDate = event.start_date_time || event.start_date
  const endDate = event.end_date_time || event.end_date
  // Show each event's dates in ITS OWN timezone, not the viewer's — a
  // Pacific event viewed from Eastern must still read as its Pacific day.
  const fmtDate = (iso?: string) => formatInEventZone(iso, event.timezone)

  return (
    <Link
      to="/events/$eventId/dashboard"
      params={{ eventId: String(event.id) }}
      className="block w-full text-left bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
    >
      <div className="flex items-start p-4 gap-3">
        <div className="flex-shrink-0">
          {event.logo ? (
            <img
              src={`${event.logo}?width=300`}
              alt={event.name}
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-md object-contain bg-white dark:bg-[#131313]"
            />
          ) : (
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-neutral-100 dark:bg-neutral-800 rounded-md flex items-center justify-center">
              <Star className="w-7 h-7 sm:w-8 sm:h-8 text-neutral-400" strokeWidth={1} />
            </div>
          )}
        </div>
        <div className="flex-grow min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-neutral-100 truncate">
            {event.name}
          </h3>
          <p className="text-xs sm:text-sm text-neutral-700 dark:text-neutral-300 mt-1">
            {startDate && endDate ? (
              fmtDate(startDate) === fmtDate(endDate) ? (
                fmtDate(startDate)
              ) : (
                <>
                  {fmtDate(startDate)} - {fmtDate(endDate)}
                </>
              )
            ) : startDate ? (
              fmtDate(startDate)
            ) : (
              'Date not specified'
            )}
          </p>
          {event.organization && (
            <p className="text-xs sm:text-xs text-neutral-500 dark:text-neutral-400 font-medium truncate mt-0.5">
              {typeof event.organization === 'object' ? event.organization.name : ''}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 self-center ml-2">
          <ChevronRight className="w-5 h-5 text-neutral-400" />
        </div>
      </div>
    </Link>
  )
}
